import {
  DrawerContent,
  LoadingCircle,
  FormGroup,
  DrawerActions,
  Checkbox,
  Button,
  ButtonVariant,
} from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  Account,
  AccountSignature,
  InvoicingSystemTypes,
  ZuoraPaymentMethodTypes,
  Location,
} from "@/api/opb/opbTypes";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PaymentDrawerTitle as DrawerTitle } from "./PaymentDrawerTitle";
import {
  PaymentFormInput as FormInput,
  PaymentFormSelect as FormSelect,
} from "./PaymentFormInput";
import { PaymentDrawer as Drawer } from "./PaymentDrawer";
import {
  AchAccountSchema,
  AchAccountSchemaType,
} from "../schemas/AchAccountSchema";
import { FormPaymentMethodType } from "../types";
import StripePaymentMethodDrawerForm from "./StripePaymentMethodDrawerForm";

import { usePaymentSetup } from "../usePaymentSetup";

interface AddAchAccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  accountSignature?: AccountSignature;
  account?: Account;
  accountLocation?: Location;
  isFirstPaymentMethod?: boolean;
  callbackData?: object;
  redirectUrl?: string;
}

const defaultFormData: AchAccountSchemaType = {
  field_achBankABACode: "",
  field_achBankAccountNumber: "",
  field_achBankAccountType: "",
  field_achBankName: "",
  field_achBankAccountName: "",
  field_passthrough4: "", // nickname
  field_passthrough5: "", // redirect url
  field_passthrough6: "", // callback data
};

export function AddAchAccountDrawer({
  isOpen,
  onClose,
  isLoading = false,
  accountSignature,
  account,
  accountLocation,
  isFirstPaymentMethod = false,
  callbackData,
  redirectUrl,
}: AddAchAccountDrawerProps) {
  const { t } = useTranslation("paymentMethods");
  const [isDefaultPaymentChecked, setIsDefaultPaymentChecked] =
    useState(isFirstPaymentMethod);

  const {
    control,
    reset,
    trigger,
    formState: { isDirty, isValid },
  } = useForm<AchAccountSchemaType>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: defaultFormData,
    defaultValues: defaultFormData,
    resolver: yupResolver(AchAccountSchema),
  });

  useEffect(() => {
    setIsDefaultPaymentChecked(isFirstPaymentMethod);
  }, [isFirstPaymentMethod]);

  const paymentMethodInformation = () => (
    <div className="pt-4 text-sm text-grey-30">
      {t("Please note that we use stripe")}
      <ul className="list-disc list-inside">
        <li>{t("1800948598 - Stripe Payments Company")}</li>
        <li>{t("4270465600 - Stripe Payments Company")}</li>
      </ul>
    </div>
  );

  const setup = usePaymentSetup({
    requiresDefaultConfirmation: Boolean(
      account?.autopay && isDefaultPaymentChecked && !isFirstPaymentMethod
    ),
    isOpen,
    isDirty: isDirty || isDefaultPaymentChecked !== isFirstPaymentMethod,
    isValid: isValid && accountSignature != null,
    validate: async () => trigger(undefined, { shouldFocus: true }),
    onClose,
    onReset: () => {
      reset(defaultFormData);
      setIsDefaultPaymentChecked(isFirstPaymentMethod);
    },
  });
  return (
    <Drawer className="z-10" isOpen={isOpen} onClose={setup.requestClose}>
      {setup.discardDialog}
      <DrawerTitle
        className="pb-4"
        title={t("Add a new ACH account")}
        onClose={setup.requestClose}
      />
      {account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling ? (
        <StripePaymentMethodDrawerForm
          accountId={account?._id}
          paymentType={ZuoraPaymentMethodTypes.ACH}
          currencyIsoCode={accountLocation?.currencyCode}
          paymentMethodInformation={paymentMethodInformation}
          isDefaultPaymentChecked={isDefaultPaymentChecked}
          setIsDefaultPaymentChecked={setIsDefaultPaymentChecked}
        />
      ) : (
        <form
          name="AchPayment2Form"
          action={accountSignature?.paymentPageUrl}
          method="post"
          onSubmit={setup.onSubmit}
          noValidate
          className="payment-add-form"
          aria-busy={setup.isSubmitting}
          id="add_ach_form"
        >
          {isLoading ? (
            <div className="flex items-center justify-center pt-4">
              <LoadingCircle spinnerSize={30} spinnerColor="#013e3f" />
            </div>
          ) : (
            <>
              <DrawerContent>
                <input type="hidden" name="method" value="submitPage" />
                <input
                  type="hidden"
                  name="id"
                  value={accountSignature?.paymentPageId || ""}
                />
                <input
                  type="hidden"
                  name="tenantId"
                  value={accountSignature?.tenantId}
                />
                <input
                  type="hidden"
                  name="token"
                  value={accountSignature?.token}
                />
                <input
                  type="hidden"
                  name="signature"
                  value={accountSignature?.signature}
                />
                <input
                  type="hidden"
                  name="host"
                  value={window.location.origin}
                />
                <input
                  type="hidden"
                  name="field_accountId"
                  value={account?.zuoraId}
                />
                <input
                  type="hidden"
                  name="paymentGateway"
                  value={account?.paymentGateway}
                />
                <input
                  type="hidden"
                  name="field_key"
                  value={accountSignature?.publicKey}
                />
                <input type="hidden" name="field_style" value="iframe" />
                <input
                  type="hidden"
                  name="field_passthrough1"
                  value={account?._id}
                />
                <input
                  type="hidden"
                  name="field_passthrough2"
                  value={FormPaymentMethodType.ACH}
                />
                <input
                  type="hidden"
                  name="field_passthrough3"
                  value={isDefaultPaymentChecked ? "true" : "false"}
                />
                <input
                  type="hidden"
                  name="field_passthrough5"
                  value={redirectUrl}
                />
                <input
                  type="hidden"
                  name="field_passthrough6"
                  value={JSON.stringify(callbackData)}
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormGroup className="payment-form-group">
                    <FormInput
                      control={control}
                      name="field_achBankABACode"
                      rules={{ required: true }}
                      label={t("ABA/routing number")}
                      isRequired
                      data-testid="add_ach-routing_number-input"
                    />
                  </FormGroup>
                  <FormGroup className="payment-form-group">
                    <FormInput
                      control={control}
                      name="field_achBankAccountNumber"
                      rules={{ required: true }}
                      label={t("Bank account number")}
                      isRequired
                      data-testid="add_ach-account_number-input"
                    />
                  </FormGroup>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormGroup className="payment-form-group">
                    <FormSelect
                      control={control}
                      name="field_achBankAccountType"
                      options={[
                        { label: t("Checking"), value: "Checking" },
                        { label: t("Savings"), value: "Saving" },
                        {
                          label: t("Business checking"),
                          value: "BusinessChecking",
                        },
                      ]}
                      rules={{ required: true }}
                      placeholder={t("Select account type")}
                      label={t("Account type")}
                      isRequired
                      data-testid="add_ach-account-select"
                    />
                  </FormGroup>
                  <FormGroup className="payment-form-group">
                    <FormInput
                      control={control}
                      name="field_achBankName"
                      rules={{ required: true }}
                      label={t("Bank name")}
                      isRequired
                      data-testid="add_ach-bank_name-input"
                    />
                  </FormGroup>
                </div>
                <FormGroup className="payment-form-group">
                  <FormInput
                    control={control}
                    name="field_achBankAccountName"
                    rules={{ required: true }}
                    label={t("Account holder name")}
                    isRequired
                    data-testid="add_ach-holder_name-input"
                  />
                </FormGroup>
                <FormGroup className="payment-form-group">
                  <FormInput
                    control={control}
                    name="field_passthrough4"
                    label={t("Nickname")}
                    data-testid="add_ach-nickname-input"
                  />
                </FormGroup>
                {paymentMethodInformation()}
              </DrawerContent>
              <DrawerActions>
                <div className="w-full grow">
                  <Checkbox
                    label={t("Set as default payment method")}
                    subtitle={t(
                      isFirstPaymentMethod
                        ? "The first payment method you add will be set as the default for your account."
                        : "Default payment message"
                    )}
                    onChange={() =>
                      setIsDefaultPaymentChecked(!isDefaultPaymentChecked)
                    }
                    checked={isDefaultPaymentChecked}
                    data-testid="add_ach-default-checkbox"
                    isDisabled={isFirstPaymentMethod}
                  />
                  <div className="payment-drawer-buttons">
                    <Button
                      type="button"
                      variant={ButtonVariant.Outline}
                      isDisabled={setup.isSubmitting}
                      onClick={setup.requestClose}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      className="w-full py-5 font-hanken sm:w-56"
                      variant={ButtonVariant.Primary}
                      type="submit"
                      isLoading={setup.isSubmitting}
                      isDisabled={
                        setup.isSubmitting || accountSignature == null
                      }
                    >
                      {t("Add Payment Method")}
                    </Button>
                  </div>
                </div>
              </DrawerActions>
            </>
          )}
        </form>
      )}
    </Drawer>
  );
}
